import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_ROUTES, API_DOMAIN, headers } from '../Components/Reusable_Components/Config/apiConfig';
import { useToast } from './ToastContext';
import { fmtDate } from '../utils/fmt';

export const NotificationContext = createContext(null);

const POLL_INTERVAL_MS = 60000;

export const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return fmtDate(dateStr);
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();
  const seenIds = useRef(null);

  const fetchNotifications = useCallback(async () => {
    const token = localStorage.getItem('customer_management_token');
    if (!token) return;

    try {
      const response = await axios.get(`${API_DOMAIN}${API_ROUTES.notifications}`, { headers });
      const items = Array.isArray(response.data) ? response.data : (response.data?.data ?? []);

      if (seenIds.current) {
        const newUnread = items.filter(n => !n.is_read && !seenIds.current.has(n.id));
        newUnread.forEach(n => addToast({ type: 'info', title: 'New notification', message: n.message }));
      }
      seenIds.current = new Set(items.map(n => n.id));

      setNotifications(items);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(() => {
      if (!document.hidden) {
        fetchNotifications();
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = useCallback(async (id) => {
    const prevNotifications = notifications;
    setNotifications(prev => prev.map(n => (n.id === id ? { ...n, is_read: true } : n)));
    try {
      await axios.patch(`${API_DOMAIN}${API_ROUTES.notifications}${id}/read/`, {}, { headers });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      setNotifications(prevNotifications);
      addToast({ type: 'error', title: 'Error', message: 'Failed to mark notification as read' });
    }
  }, [notifications, addToast]);

  const markAllAsRead = useCallback(async () => {
    const prevNotifications = notifications;
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    try {
      await axios.patch(`${API_DOMAIN}${API_ROUTES.notifications}mark-all-read/`, {}, { headers });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      setNotifications(prevNotifications);
      addToast({ type: 'error', title: 'Error', message: 'Failed to mark all notifications as read' });
    }
  }, [notifications, addToast]);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <NotificationContext.Provider
      value={{ notifications, unreadCount, loading, markAsRead, markAllAsRead, refresh: fetchNotifications }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};
